import Keycloak from 'keycloak-js';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { AppConfig, Organisation } from '../config';
import { prefs, readPref, writePref } from '../storage';

// Sign-in: the person picks their organisation, and we send them to that organisation's own realm
// (endurance-<org>). Brokered logins through endurance-platform are refused by the KB
// (BrokeredLoginFilter, docs/AUTH_SCHEMA.md §4.8), so the realm choice has to happen here.

export type AuthStatus = 'loading' | 'signedOut' | 'signedIn' | 'redirecting';

export interface Profile {
  displayName: string;
  username: string;
  email: string;
}

export interface AuthState {
  status: AuthStatus;
  organisation?: Organisation;
  /** Organisation remembered on this device, offered first on the sign-in page. */
  rememberedOrganisation?: Organisation;
  profile?: Profile;
  error?: string;
  signIn(organisation: Organisation): Promise<void>;
  signOut(): Promise<void>;
  /** Forget the remembered organisation and sign out if needed. */
  changeOrganisation(): Promise<void>;
  /** Fresh access token (refreshed when under 30 s left) or undefined when signed out. */
  getToken(): Promise<string | undefined>;
}

const AuthContext = createContext<AuthState | null>(null);

export type KeycloakFactory = (options: { url: string; realm: string; clientId: string }) => Keycloak;
const defaultFactory: KeycloakFactory = (options) => new Keycloak(options);

function profileFrom(kc: Keycloak): Profile {
  const claims = (kc.tokenParsed ?? {}) as Record<string, unknown>;
  const username = typeof claims.preferred_username === 'string' ? claims.preferred_username : '';
  return {
    displayName: typeof claims.name === 'string' && claims.name ? claims.name : username || 'Signed-in user',
    username,
    email: typeof claims.email === 'string' ? claims.email : '',
  };
}

export function AuthProvider({
  config,
  children,
  keycloakFactory = defaultFactory,
}: {
  config: AppConfig;
  children: ReactNode;
  keycloakFactory?: KeycloakFactory;
}) {
  const findOrg = useCallback(
    (realm: string | null) => config.organisations.find((o) => o.realm === realm),
    [config.organisations],
  );
  // With no remembered organisation there is no session to look for: show the sign-in page at once.
  const [status, setStatus] = useState<AuthStatus>(() => (findOrg(readPref(prefs.realm)) ? 'loading' : 'signedOut'));
  const [organisation, setOrganisation] = useState<Organisation | undefined>();
  const [remembered, setRemembered] = useState<Organisation | undefined>(() => findOrg(readPref(prefs.realm)));
  const [profile, setProfile] = useState<Profile | undefined>();
  const [error, setError] = useState<string | undefined>();
  const keycloak = useRef<Keycloak | null>(null);

  const createInstance = useCallback(
    (org: Organisation) => keycloakFactory({ url: config.keycloakUrl, realm: org.realm, clientId: config.clientId }),
    [config.clientId, config.keycloakUrl, keycloakFactory],
  );

  // keycloak-js may be initialised only once per page: the sign-in callback's code and state are
  // single-use. React StrictMode (development) runs effects twice, so the init is started once and
  // its promise kept in a ref that survives the re-run; each run only reads the shared result.
  const bootstrap = useRef<{ kc: Keycloak; org: Organisation; ready: Promise<boolean> } | null>(null);

  // On load: if this device remembers an organisation, quietly check for an existing session
  // there. This also completes the sign-in when Keycloak redirects back with a code.
  useEffect(() => {
    if (!bootstrap.current) {
      const org = findOrg(readPref(prefs.realm));
      if (!org) return;
      const kc = createInstance(org);
      kc.onTokenExpired = () => {
        kc.updateToken(30).catch(() => setStatus('signedOut'));
      };
      const ready = kc.init({
        onLoad: 'check-sso',
        pkceMethod: 'S256',
        checkLoginIframe: false,
        silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
      });
      bootstrap.current = { kc, org, ready };
    }
    const { kc, org, ready } = bootstrap.current;
    let cancelled = false;
    ready
      .then((authenticated) => {
        if (cancelled) return;
        keycloak.current = kc;
        setOrganisation(org);
        setProfile(authenticated ? profileFrom(kc) : undefined);
        setStatus(authenticated ? 'signedIn' : 'signedOut');
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        // Typically the token exchange blocked by CORS (origin missing from the client's web
        // origins) or an invalid redirect. Logged for whoever is diagnosing; the page says it in words.
        console.error('Keycloak sign-in could not be completed', cause);
        setError('keycloak');
        setStatus('signedOut');
      });
    return () => {
      cancelled = true;
    };
  }, [createInstance, findOrg]);

  const signIn = useCallback(
    async (org: Organisation) => {
      writePref(prefs.realm, org.realm);
      setRemembered(org);
      setStatus('redirecting');
      const kc = createInstance(org);
      await kc.init({ pkceMethod: 'S256', checkLoginIframe: false });
      await kc.login({ redirectUri: window.location.href });
    },
    [createInstance],
  );

  const signOut = useCallback(async () => {
    const kc = keycloak.current;
    if (kc?.authenticated) {
      await kc.logout({ redirectUri: `${window.location.origin}/` });
      return;
    }
    setStatus('signedOut');
  }, []);

  const changeOrganisation = useCallback(async () => {
    writePref(prefs.realm, null);
    writePref(prefs.tenant, null);
    setRemembered(undefined);
    await signOut();
  }, [signOut]);

  const getToken = useCallback(async () => {
    const kc = keycloak.current;
    if (!kc?.authenticated) return undefined;
    try {
      await kc.updateToken(30);
    } catch {
      setStatus('signedOut');
      return undefined;
    }
    return kc.token;
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      status,
      organisation,
      rememberedOrganisation: remembered,
      profile,
      error,
      signIn,
      signOut,
      changeOrganisation,
      getToken,
    }),
    [status, organisation, remembered, profile, error, signIn, signOut, changeOrganisation, getToken],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth outside AuthProvider');
  return value;
}

/** For tests and previews: provide a fixed auth state without Keycloak. */
export const TestAuthContext = AuthContext;
